import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { styled } from 'styled-components';

import { Link } from '@sorare/routing';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import { Dot } from '@sorare/core/src/atoms/icons/Dot';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelL } from '@sorare/core/src/atoms/typography';
import { Card } from '@sorare/core/src/components/card/Card';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { getPositionInitials } from '@sorare/core/src/lib/positions';

import { CardBonus } from '@sorare/marketplace/src/components/card/CardBonus';

import { calculateScoreWithBonus } from 'lib/sports';

import type { YourCard_anyCardInterface } from './__generated__/index.graphql';

const CardWrapper = styled.div`
  width: calc(10 * var(--unit));
`;

type Props = {
  card: YourCard_anyCardInterface;
};

export const YourCard = ({ card }: Props) => {
  const { generateSportContextPath } = useSportContext();

  return (
    <SBHorizontal key={card.slug}>
      <Link
        to={generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
          params: {
            slug: card.anyPlayer.slug,
          },
        })}
      >
        <Horizontal>
          <CardWrapper>
            <Card card={card} width={80} />
          </CardWrapper>
          <Vertical>
            <LabelL bold>{card.anyPlayer.displayName}</LabelL>
            <Horizontal gap={0.5}>
              <img src={card.anyTeam.pictureUrl || ''} alt="" height={16} />
              <BodyS color="var(--c-nd-600)">{card.anyTeam.shortName}</BodyS>
              <BodyS color="var(--c-nd-600)">
                <Dot size={0.5} />
              </BodyS>
              <BodyS color="var(--c-nd-600)">
                {card.anyPositions
                  .map(position => getPositionInitials(position))
                  .join(', ')}
              </BodyS>
            </Horizontal>

            <CardBonus card={card} />
          </Vertical>
        </Horizontal>
      </Link>
      <Horizontal>
        <Score
          hideReviewing
          shape="PILL"
          score={
            calculateScoreWithBonus(
              parseFloat(card.power),
              card.anyPlayer.playerGameScore?.score ?? 0
            ).score
          }
          scoreStatus={
            card.anyPlayer.playerGameScore?.scoreStatus ?? ScoreStatus.FINAL
          }
          scoreType="CARD_SCORE"
          isCappedMode={false}
          isAffordable={false}
          animated={false}
          inverted={false}
        />
      </Horizontal>
    </SBHorizontal>
  );
};

YourCard.fragments = {
  anyCardInterface: gql`
    fragment YourCard_anyCardInterface on AnyCardInterface {
      slug
      rarityTyped
      anyTeam {
        slug
        shortName
        pictureUrl
      }
      power
      anyPlayer {
        slug
        displayName
        anyPositions
        sport
        playerGameScore(gameId: $gameIdWithoutPrefix) {
          id
          score
          scoreStatus
        }
      }
      ...Card_anyCard
      ...CardBonus_anyCard
    }
    ${Card.fragments.anyCard}
    ${CardBonus.fragments.anyCard}
  ` as TypedDocumentNode<YourCard_anyCardInterface>,
};
